import { FormRow, FormSection, BigFormRow } from 'framework/ui/layout';
import React from 'react';
import { TextInput, DateInput, InputList, Dropdown, NumberInput, useFormContext } from 'framework/form';
import TicketInput from 'domain/gpo/oneStop/workloadReportingIssues/TicketInput';
import TimeEntryInput from 'domain/gpo/oneStop/workloadReportingIssues/TimeEntryInput';
import { useSearchParameter } from 'framework/routing';
import { requestTypeOptions, activityOptions } from './constants';
import { OneStopPageLayout } from 'domain/shared/oneStop';
import { Grid2 as Grid } from '@mui/material';
import useWorkloadReportingIssuesService from 'domain/gpo/oneStop/workloadReportingIssues/useWorkloadReportingIssuesService';
import { OneStopCreateForm } from 'domain/shared/oneStop';

type OneStopWorkloadReportingIssuesProps = {
  openModal: (message: string) => void;
  requestId?: string;
};

const ChangeItem: React.FC<any> = inputProps => {
  const { source } = inputProps;

  return (
    <Grid container spacing={2}>
      {/* Current entry row */}
      <Grid size={12}>
        <FormRow>
          <TicketInput
            source={`${source}.ticketId`}
            correctTicketIdSource={`${source}.correctTicketId`}
            label="Ticket"
            isRequired
            placeholder="e.g. 975216"
          />
          <TimeEntryInput
            source={`${source}.timeEntryId`}
            issueIdSource={`${source}.ticketId`}
            trackingDateSource={`${source}.trackingDate`}
            currentTimeTrackSource={`${source}.currentTimeTrack`}
            correctTrackingDateSource={`${source}.correctTrackingDate`}
            correctTimeTrackSource={`${source}.correctTimeTrack`}
            label="Time entry"
            isRequired
            placeholder="Select time entry"
          />
          <DateInput source={`${source}.trackingDate`} label="Tracking date" isRequired disabled />
          <TextInput
            source={`${source}.currentTimeTrack`}
            label="Current time track, h"
            isRequired
            placeholder="e.g. 2:15"
            disabled
          />
        </FormRow>
      </Grid>

      {/* Corrected values row */}
      <Grid size={12}>
        <FormRow>
          <TicketInput
            source={`${source}.correctTicketId`}
            label="Correct ticket ID"
            placeholder="Enter correct ticket ID"
          />
          <DateInput source={`${source}.correctTrackingDate`} label="Correct tracking date" />
          <NumberInput
            source={`${source}.correctTimeTrack`}
            label="Correct time track, h"
            placeholder="e.g. 1.5"
            slotProps={{ htmlInput: { min: 0 } }}
          />
        </FormRow>
      </Grid>
    </Grid>
  );
};

const MissedTrackingItem: React.FC<any> = inputProps => {
  const { source } = inputProps;
  const { onChangeCallback } = useFormContext();

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 2 }} sx={{ flexShrink: 1 }}>
            <TicketInput
              source={`${source}.ticketId`}
              correctTicketIdSource={`${source}.correctTicketId`}
              label="Ticket ID"
              isRequired
              placeholder="e.g. 975216"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }} sx={{ flexShrink: 1 }}>
            <DateInput
              source={`${source}.trackingDate`}
              label="Tracking date"
              isRequired
              onChangeCallback={values => {
                const value = values[`${source}.trackingDate`];
                onChangeCallback?.({
                  [`${source}.trackingDate`]: value,
                  [`${source}.correctTrackingDate`]: value,
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }} sx={{ flexShrink: 1 }}>
            <NumberInput
              source={`${source}.currentTimeTrack`}
              label="Time track, h"
              isRequired
              placeholder="e.g. 2.5"
              slotProps={{ htmlInput: { min: 0 } }}
              onChangeCallback={values => {
                const value = values[`${source}.currentTimeTrack`];
                onChangeCallback?.({
                  [`${source}.currentTimeTrack`]: value,
                  [`${source}.correctTimeTrack`]: value,
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }} sx={{ flexShrink: 1 }}>
            <Dropdown source={`${source}.activity`} label="Activity" options={activityOptions} isRequired />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ flexShrink: 1 }}>
            <TextInput
              source={`${source}.descriptionOfWork`}
              label="Description of work"
              placeholder="Investigate issues"
              isRequired
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ChangesList = () => {
  const { data: formData = {} } = useFormContext();
  const requestType = formData.requestType;
  const isMissedTracking = requestType === 'NEW_TIME_ENTRY';

  return (
    <InputList
      source="changes"
      addText="Add Item"
      inputTemplate={isMissedTracking ? MissedTrackingItem : ChangeItem}
      defaultValue={{}}
      maxFields={100}
    />
  );
};
const OneStopWorkloadReportingIssues = ({
  openModal,
  requestId = 'WORKLOAD_REPORTING_ISSUES',
}: OneStopWorkloadReportingIssuesProps) => {
  const { createRequest, getValidationSchema } = useWorkloadReportingIssuesService();

  const initialQueryParam = useSearchParameter('initial');

  const parsedInitialData = React.useMemo(() => {
    if (!initialQueryParam) {
      return null;
    }

    try {
      return JSON.parse(initialQueryParam);
    } catch {
      return null;
    }
  }, [initialQueryParam]);

  const initialData = React.useMemo(() => {
    const base = {
      requestType: 'EXISTING_TIME_ADJUSTMENT',
      changes: [],
    };
    if (parsedInitialData) {
      return { ...base, ...parsedInitialData };
    }
    return base;
  }, [parsedInitialData]);

  return (
    <OneStopCreateForm
      requestId={requestId}
      submitFormFunc={createRequest}
      getValidationSchemaFunc={getValidationSchema}
      afterSubmit={{
        redirect: '/one-stop/requests',
        execute: () => {
          openModal('Request has been successfully submitted!');
        },
      }}
      initialData={initialData}
    >
      <OneStopPageLayout>
        <FormSection title="Time track information">
          <FormRow>
            <Dropdown source="requestType" label="Request type" options={requestTypeOptions} isRequired />
            <TextInput source="reasonForChange" label="Reason for change" isRequired />
          </FormRow>
        </FormSection>

        <FormSection title="Details of changes">
          <BigFormRow>
            <ChangesList />
          </BigFormRow>
        </FormSection>
      </OneStopPageLayout>
    </OneStopCreateForm>
  );
};

export default OneStopWorkloadReportingIssues;
